import { useState, type FormEvent } from "react";
import { useNavigate, Link } from "react-router-dom";
import { httpsCallable } from "firebase/functions";
import { signInWithEmailAndPassword } from "firebase/auth";
import { auth, functions } from "./firebase";
import { Input, Button, FormCard } from "./FormComponents";
import { useToast } from "./hooks/useToast";
import { PasswordInput } from "./components/PasswordInput";
import { UninvitedUserModal } from "./components/UninvitedUserModal";

export default function SignUp() {
  const navigate = useNavigate();
  const toast = useToast();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showUninvitedModal, setShowUninvitedModal] = useState(false);

  // Function to complete the signup process
  const performSignup = async (confirmedUninvited = false) => {
    setIsLoading(true);

    try {
      const completeSignup = httpsCallable(functions, "completeSignup");
      const result = await completeSignup({
        email,
        password,
        confirmedUninvited,
      });

      const data = result.data as { success: boolean; message: string; needsConfirmation?: boolean };

      if (data.needsConfirmation) {
        // User is not invited - show confirmation modal
        setIsLoading(false);
        setShowUninvitedModal(true);
        return;
      }

      if (data.success) {
        toast.success("Account created successfully! Signing you in...");

        // Automatically sign in the user
        await signInWithEmailAndPassword(auth, email, password);

        // Redirect to albums page
        navigate("/albums");
      } else {
        toast.error(data.message || "An error occurred during signup.");
      }
    } catch (err) {
      const errorMessage = (err as { message?: string })?.message || "An unexpected error occurred.";
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Validate password match
    if (password !== confirmPassword) {
      toast.error("Passwords do not match.");
      return;
    }

    // Validate password length
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters.");
      return;
    }

    await performSignup(false);
  };

  // Handle user confirming they want to join without invitation
  const handleConfirmUninvited = async () => {
    setShowUninvitedModal(false);
    await performSignup(true);
  };

  // Handle user canceling the uninvited modal
  const handleCancelUninvited = () => {
    setShowUninvitedModal(false);
  };

  return (
    <>
      <FormCard title="Sign Up">
        {/* Sign In Link */}
        <p className="text-center text-base text-gray-700">
          Already have an account?{" "}
          <Link to="/login" className="font-normal text-primary-600 hover:text-primary-700 underline">
            Log In
          </Link>
        </p>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '2rem', marginTop: '1rem' }}>
          <Input
            id="email"
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="email"
            disabled={isLoading}
            variant="underline"
          />

          <PasswordInput
            id="password"
            label="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            autoComplete="new-password"
            disabled={isLoading}
          />

          <PasswordInput
            id="confirm-password"
            label="Confirm Password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            autoComplete="new-password"
            disabled={isLoading}
          />

          <Button type="submit" disabled={isLoading} variant="brown">
            {isLoading ? "Creating Account..." : "Sign Up"}
          </Button>
        </form>
      </FormCard>

      <UninvitedUserModal
        isOpen={showUninvitedModal}
        onConfirm={handleConfirmUninvited}
        onCancel={handleCancelUninvited}
      />
    </>
  );
}
